import { Controller } from "@hotwired/stimulus"

/**
 * Controller for managing item media uploads on the NEW item form.
 * Uses nested attributes pattern with hidden inputs - pure Rails approach.
 *
 * When user adds files, creates:
 * - Preview card in the UI
 * - Hidden inputs for nested attributes (file, position, span, column, media_type)
 *
 * Form submits normally with all nested attributes.
 */
export default class extends Controller {
  static targets = ["dropzone", "list", "emptyState", "counter", "errorMessage", "fieldsContainer"]
  // Note: No "input" target - file inputs are created dynamically
  static values = {
    maxFiles: { type: Number, default: 20 }
  }

  connect() {
    this.mediaIndex = 0
    this.updateUI()
  }

  openPicker() {
    // Create a temporary file input and trigger it
    const input = document.createElement("input")
    input.type = "file"
    input.multiple = true
    input.accept = "image/*,video/*"
    input.style.display = "none"
    document.body.appendChild(input)
    input.addEventListener("change", (e) => {
      this.handleFiles(e)
      input.remove() // Clean up after use
    })
    input.click()
  }

  handleDragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.add("border-indigo-500", "bg-indigo-50")
  }

  handleDragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-indigo-500", "bg-indigo-50")
  }

  handleDrop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dropzoneTarget.classList.remove("border-indigo-500", "bg-indigo-50")

    const files = event.dataTransfer.files
    if (files.length > 0) {
      this.addFiles(files)
    }
  }

  handleFiles(event) {
    const files = event.target.files
    if (files.length > 0) {
      this.addFiles(files)
    }
  }

  addFiles(fileList) {
    const errors = []
    const currentCount = this.listTarget.querySelectorAll(".media-card").length

    for (const file of fileList) {
      if (currentCount + errors.length >= this.maxFilesValue) {
        errors.push(`Maximum ${this.maxFilesValue} fichiers autorisés`)
        break
      }

      const validation = this.validateFile(file)
      if (!validation.valid) {
        errors.push(`${file.name}: ${validation.error}`)
        continue
      }

      this.createMediaEntry(file)
    }

    if (errors.length > 0) {
      this.showError(errors.join(", "))
    }

    this.updatePositions()
    this.updateUI()
  }

  validateFile(file) {
    const validTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "video/mp4", "video/webm"]
    const maxSize = 50 * 1024 * 1024 // 50MB

    if (!validTypes.includes(file.type)) {
      return { valid: false, error: "Format non supporté (jpg, png, webp, gif, mp4, webm)" }
    }

    if (file.size > maxSize) {
      return { valid: false, error: "Fichier trop volumineux (max 50MB)" }
    }

    return { valid: true }
  }

  createMediaEntry(file) {
    const index = this.mediaIndex++
    const mediaType = file.type.startsWith("video/") ? "video" : "photo"
    const isVideo = mediaType === "video"

    // Create hidden fields container for this media
    const fieldsDiv = document.createElement("div")
    fieldsDiv.className = "media-fields"
    fieldsDiv.dataset.mediaIndex = index

    // Create file input (hidden, but contains the actual file)
    const fileInput = document.createElement("input")
    fileInput.type = "file"
    fileInput.name = `item[item_medias_attributes][${index}][file]`
    fileInput.className = "hidden"
    // Transfer the file to this input
    const dataTransfer = new DataTransfer()
    dataTransfer.items.add(file)
    fileInput.files = dataTransfer.files

    // Hidden inputs for attributes
    const mediaTypeInput = this.createHiddenInput(`item[item_medias_attributes][${index}][media_type]`, mediaType)
    const positionInput = this.createHiddenInput(`item[item_medias_attributes][${index}][position]`, "1")
    positionInput.dataset.positionInput = "true"
    const columnInput = this.createHiddenInput(`item[item_medias_attributes][${index}][grid_column]`, "1")
    const spanInput = this.createHiddenInput(`item[item_medias_attributes][${index}][span]`, "1")
    spanInput.dataset.spanInput = "true"

    fieldsDiv.appendChild(fileInput)
    fieldsDiv.appendChild(mediaTypeInput)
    fieldsDiv.appendChild(positionInput)
    fieldsDiv.appendChild(columnInput)
    fieldsDiv.appendChild(spanInput)

    this.fieldsContainerTarget.appendChild(fieldsDiv)

    // Create preview card
    const card = this.createPreviewCard(file, index, isVideo)
    this.listTarget.appendChild(card)
  }

  createHiddenInput(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  createPreviewCard(file, index, isVideo) {
    const card = document.createElement("div")
    card.className = "media-card relative group bg-gray-100 rounded-lg overflow-hidden cursor-move aspect-square"
    card.dataset.mediaIndex = index
    card.draggable = true

    // Drag events
    card.addEventListener("dragstart", (e) => this.handleCardDragStart(e, card))
    card.addEventListener("dragend", (e) => this.handleCardDragEnd(e, card))
    card.addEventListener("dragover", (e) => this.handleCardDragOver(e, card))
    card.addEventListener("drop", (e) => this.handleCardDrop(e, card))

    // Preview content
    let previewHtml
    if (isVideo) {
      previewHtml = `
        <div class="flex items-center justify-center h-full bg-gray-800">
          <i class="fa-solid fa-play text-white text-4xl"></i>
        </div>
      `
    } else {
      const previewUrl = URL.createObjectURL(file)
      previewHtml = `<img src="${previewUrl}" class="w-full h-full object-cover" alt="${this.escapeHtml(file.name)}">`
    }

    card.innerHTML = `
      ${previewHtml}
      <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex flex-col justify-between p-2">
        <div class="flex justify-between items-start">
          <div class="opacity-0 group-hover:opacity-100 transition-opacity">
            <i class="fa-solid fa-grip-vertical text-white text-lg"></i>
          </div>
          <button type="button"
                  class="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                  data-action="click->new-item-media#removeMedia"
                  data-media-index="${index}">
            <i class="fa-solid fa-times text-sm"></i>
          </button>
        </div>
        <div class="flex justify-between items-end">
          <div class="bg-gray-800 text-white text-xs px-2 py-1 rounded-full position-badge">
            #1
          </div>
          <div class="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            <button type="button"
                    class="layout-btn portrait-btn w-8 h-8 rounded-full flex items-center justify-center transition-colors bg-blue-600 text-white"
                    data-action="click->new-item-media#setLayout"
                    data-layout="portrait"
                    data-media-index="${index}"
                    title="Portrait (1 colonne)">
              <i class="fa-solid fa-arrows-up-down"></i>
            </button>
            <button type="button"
                    class="layout-btn landscape-btn w-8 h-8 rounded-full flex items-center justify-center transition-colors bg-white text-gray-700 hover:bg-gray-100"
                    data-action="click->new-item-media#setLayout"
                    data-layout="landscape"
                    data-media-index="${index}"
                    title="Paysage (2 colonnes)">
              <i class="fa-solid fa-arrows-left-right"></i>
            </button>
          </div>
        </div>
      </div>
    `

    return card
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  removeMedia(event) {
    event.preventDefault()
    event.stopPropagation()

    const index = event.currentTarget.dataset.mediaIndex

    // Remove hidden fields
    const fields = this.fieldsContainerTarget.querySelector(`[data-media-index="${index}"]`)
    if (fields) fields.remove()

    // Remove preview card
    const card = this.listTarget.querySelector(`[data-media-index="${index}"]`)
    if (card) card.remove()

    this.updatePositions()
    this.updateUI()
  }

  setLayout(event) {
    event.preventDefault()
    event.stopPropagation()

    const index = event.currentTarget.dataset.mediaIndex
    const layout = event.currentTarget.dataset.layout
    const span = layout === "landscape" ? "2" : "1"

    // Update hidden span input
    const fields = this.fieldsContainerTarget.querySelector(`[data-media-index="${index}"]`)
    if (fields) {
      const spanInput = fields.querySelector('[data-span-input="true"]')
      if (spanInput) spanInput.value = span
    }

    // Update card appearance
    const card = this.listTarget.querySelector(`[data-media-index="${index}"]`)
    if (card) {
      if (span === "2") {
        card.classList.add("col-span-2")
      } else {
        card.classList.remove("col-span-2")
      }

      // Update button states
      const portraitBtn = card.querySelector(".portrait-btn")
      const landscapeBtn = card.querySelector(".landscape-btn")
      if (portraitBtn && landscapeBtn) {
        if (span === "2") {
          portraitBtn.className = "layout-btn portrait-btn w-8 h-8 rounded-full flex items-center justify-center transition-colors bg-white text-gray-700 hover:bg-gray-100"
          landscapeBtn.className = "layout-btn landscape-btn w-8 h-8 rounded-full flex items-center justify-center transition-colors bg-blue-600 text-white"
        } else {
          portraitBtn.className = "layout-btn portrait-btn w-8 h-8 rounded-full flex items-center justify-center transition-colors bg-blue-600 text-white"
          landscapeBtn.className = "layout-btn landscape-btn w-8 h-8 rounded-full flex items-center justify-center transition-colors bg-white text-gray-700 hover:bg-gray-100"
        }
      }
    }
  }

  // Drag and drop reordering
  handleCardDragStart(event, card) {
    this.draggedCard = card
    card.classList.add("opacity-50", "scale-95")
    event.dataTransfer.effectAllowed = "move"
  }

  handleCardDragEnd(event, card) {
    card.classList.remove("opacity-50", "scale-95")
    this.listTarget.querySelectorAll(".media-card").forEach(c => {
      c.classList.remove("border-indigo-500", "border-2")
    })
    this.draggedCard = null
  }

  handleCardDragOver(event, card) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    if (this.draggedCard && this.draggedCard !== card) {
      card.classList.add("border-indigo-500", "border-2")
    }
  }

  handleCardDrop(event, card) {
    event.preventDefault()
    card.classList.remove("border-indigo-500", "border-2")

    if (!this.draggedCard || this.draggedCard === card) return

    // Reorder in DOM
    const cards = Array.from(this.listTarget.querySelectorAll(".media-card"))
    const draggedIndex = cards.indexOf(this.draggedCard)
    const targetIndex = cards.indexOf(card)

    if (draggedIndex < targetIndex) {
      card.parentNode.insertBefore(this.draggedCard, card.nextSibling)
    } else {
      card.parentNode.insertBefore(this.draggedCard, card)
    }

    this.updatePositions()
  }

  updatePositions() {
    const cards = this.listTarget.querySelectorAll(".media-card")
    cards.forEach((card, idx) => {
      const position = idx + 1
      const mediaIndex = card.dataset.mediaIndex

      // Update hidden position input
      const fields = this.fieldsContainerTarget.querySelector(`[data-media-index="${mediaIndex}"]`)
      if (fields) {
        const positionInput = fields.querySelector('[data-position-input="true"]')
        if (positionInput) positionInput.value = position
      }

      // Update position badge
      const badge = card.querySelector(".position-badge")
      if (badge) badge.textContent = `#${position}`
    })
  }

  updateUI() {
    const count = this.listTarget.querySelectorAll(".media-card").length

    // Update counter
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `(${count})`
    }

    // Toggle empty state
    if (this.hasEmptyStateTarget) {
      if (count > 0) {
        this.emptyStateTarget.classList.add("hidden")
      } else {
        this.emptyStateTarget.classList.remove("hidden")
      }
    }
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("hidden")
      setTimeout(() => {
        this.errorMessageTarget.classList.add("hidden")
      }, 5000)
    }
  }
}
